import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useStackHealth } from '../../hooks/useStackHealth';
import type { HealthSignal, HealthStatus } from '../../types/health';

const STATUS_COLORS: Record<HealthStatus, string> = {
  green: 'text-emerald-400',
  amber: 'text-amber-400',
  red: 'text-red-400',
};

const DOT_COLORS: Record<HealthStatus, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

function relativeTime(isoString: string): string {
  if (!isoString) return 'never';
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

const SignalRow: React.FC<{ signal: HealthSignal; darkMode: boolean }> = ({ signal, darkMode }) => {
  const [expanded, setExpanded] = useState(false);

  const detailEntries = Object.entries(signal.detail).filter(
    ([k]) => k !== 'status' && k !== 'summary'
  );

  return (
    <div
      className={`border-l-4 ${
        signal.status === 'green' ? 'border-emerald-500' :
        signal.status === 'amber' ? 'border-amber-500' : 'border-red-500'
      } pl-3 py-2 cursor-pointer`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${DOT_COLORS[signal.status]}`} />
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            {signal.label}
          </span>
          <span className={`text-xs ${STATUS_COLORS[signal.status]}`}>
            {signal.summary}
          </span>
        </div>
        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {expanded ? '\u25BC' : '\u25B6'}
        </span>
      </div>

      {expanded && detailEntries.length > 0 && (
        <div className={`mt-2 pl-4 space-y-1 font-mono text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {detailEntries.map(([key, value]) => (
            <div key={key} className="flex gap-2">
              <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{key}:</span>
              <span className={darkMode ? 'text-emerald-300' : 'text-emerald-700'}>
                {typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StackHealthPanel: React.FC = () => {
  const { darkMode } = useTheme();
  const { signals, overall, checkedAt, source, loading, error, refresh } = useStackHealth();
  const [relTime, setRelTime] = useState('');

  // Live-updating relative time
  useEffect(() => {
    const update = () => setRelTime(relativeTime(checkedAt));
    update();
    const interval = setInterval(update, 5000);
    return () => clearInterval(interval);
  }, [checkedAt]);

  if (loading) {
    return (
      <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
        <div className="animate-pulse h-32" />
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      {/* SEEDWARE header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`font-mono text-xs px-2 py-0.5 rounded ${
            darkMode ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
          }`}>
            [SEEDWARE::health]
          </span>
          <span className={`text-xs font-semibold uppercase tracking-wider ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Stack Health
          </span>
        </div>
        <button
          onClick={() => refresh()}
          className={`text-xs px-2 py-1 rounded transition-colors ${
            darkMode
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          &#x21BB; Refresh
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="text-xs text-red-400 mb-3">{error}</div>
      )}

      {/* Signal rows */}
      <div className="space-y-2">
        {signals.map((signal) => (
          <SignalRow key={signal.name} signal={signal} darkMode={darkMode} />
        ))}
      </div>

      {/* Footer */}
      <div className={`mt-4 pt-3 border-t flex items-center justify-between text-xs ${
        darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'
      }`}>
        <span>Last checked: {relTime}</span>
        {source === 'memory' && (
          <span className="text-amber-400 font-mono">&#x26A0; serving from memory (Redis unavailable)</span>
        )}
      </div>
    </div>
  );
};
